/* eslint-disable no-nested-ternary */
/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { Stack, Grid, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { useSelector } from 'react-redux';
import {
  selectDataLastUpdated,
  selectEnableHotelGroups,
  selectGroupPmsDates,
  selectHotelName,
  selectLastFullBookingDate,
  selectLastPartialBookingDate,
  selectLocalCurrency,
  selectPartialHours,
  selectPmsDate,
} from 'modules/dashboard/selectors';
import { format, max, parseISO } from 'date-fns';
import { useEffect, useState } from 'react';
import { mapFooterData } from 'modules/dashboard/functions';
import {
  YEAR_MONTH_DAY_HOUR_MINUTE_TZ_PATTERN,
  YEAR_MONTH_DAY_PATTERN,
} from '../../../../common/constants/date-range';
import BookingMetaDataCard from './booking-meta-data-card';

/**
 * Data updated labels for each widget
 * @param {Date} latestDate - Latest booking date of selected hotel
 * @param {String} hotelTimezone - Timezone of selected hotel
 * @returns
 */
const DataUpdatedLabel = ({ hotelTimezone, toggleDisabled }) => {
  const lastUpdatedDate = useSelector(selectDataLastUpdated);
  const pmsDate = useSelector(selectPmsDate);
  const groupPmsDates = useSelector(selectGroupPmsDates);
  const enableHotelGroups = useSelector(selectEnableHotelGroups);
  const lastFullBookingDate = useSelector(selectLastFullBookingDate);
  const partialHours = useSelector(selectPartialHours);
  const lastPartialBookingDate = useSelector(selectLastPartialBookingDate);
  const hotel = useSelector(selectHotelName);
  const currencyType = useSelector(selectLocalCurrency);
  const [fullBookingMetaData, setFullBookingMetaData] = useState();
  const [partialBookingMetaData, setPartialBookingMetaData] = useState();
  //
  const lastUpdatedDateUtc = DateTime.fromSQL(lastUpdatedDate, { zone: 'utc' });
  const formattedLastUpdatedDate = lastUpdatedDateUtc
    .setZone(hotelTimezone)
    .toFormat(YEAR_MONTH_DAY_HOUR_MINUTE_TZ_PATTERN);
  // Calculate booking metadata for full and partial pickup. If hotel group is enabled, aggregate the metadata across all hotels in the group.
  useEffect(() => {
    // Helper function to aggregate booking metadata across multiple hotels
    const aggregate = (data = []) => {
      const totals = data.reduce(
        (acc, item) => {
          acc.rooms += item.rooms || 0;
          acc.revenue += item.revenue || 0;
          return acc;
        },
        { rooms: 0, revenue: 0 }
      );

      return {
        ...totals,
        adr: totals.rooms ? totals.revenue / totals.rooms : 0,
      };
    };
    // If booking metadata is available at the hotel level, use it directly. Otherwise, aggregate from hotels in the group.
    if (hotel?.bookingMetaData) {
      const full = mapFooterData(hotel.bookingMetaData?.full, currencyType);
      const partial = mapFooterData(hotel.bookingMetaData?.partial, currencyType);
      //
      setFullBookingMetaData(full);
      setPartialBookingMetaData(partial);
      return;
    }

    const hotels = hotel?.hotels;
    if (!hotels?.length) return;

    const fullData = hotels
      .map((h) => h.bookingMetaData?.full)
      .filter(Boolean)
      .map((m) => mapFooterData(m, currencyType));

    const partialData = hotels
      .map((h) => h.bookingMetaData?.partial)
      .filter(Boolean)
      .map((m) => mapFooterData(m, currencyType));

    setFullBookingMetaData(aggregate(fullData));
    setPartialBookingMetaData(aggregate(partialData));
  }, [hotel, currencyType]);
  //
  return (
    <Stack
      direction="column"
      spacing={1}
      xs={12}
      paddingBottom={2}
      alignSelf="center"
      marginLeft={2}
    >
      <BookingMetaDataCard
        title="Last avail Booking Date pickup:"
        date={`${lastPartialBookingDate} (UTC, partial ${partialHours} hours)`}
        revenue={partialBookingMetaData?.revenue}
        rooms={partialBookingMetaData?.rooms}
        adr={partialBookingMetaData?.adr}
      />
      <BookingMetaDataCard
        title="Last full Booking Date pickup:"
        date={`${lastFullBookingDate} (UTC, full 24 hours)`}
        revenue={fullBookingMetaData?.revenue}
        rooms={fullBookingMetaData?.rooms}
        adr={fullBookingMetaData?.adr}
      />
      <Grid container direction="column">
        <Grid item>
          <Typography
            sx={{ fontSize: 11, color: '#9e9e9e', whiteSpace: 'pre-wrap' }}
            variant="inherit"
          >
            Data updated:{'      '}
          </Typography>
        </Grid>
        <Grid item>
          <Grid container direction="column">
            <Grid item>
              <Typography sx={{ fontSize: 11 }}>
                {lastUpdatedDate
                  ? lastUpdatedDateUtc.toFormat(YEAR_MONTH_DAY_HOUR_MINUTE_TZ_PATTERN)
                  : ''}
              </Typography>
            </Grid>
            <Grid item>
              <Typography sx={{ fontSize: 11 }}>
                {lastUpdatedDate ? `${formattedLastUpdatedDate} (local)` : ''}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      {!toggleDisabled && (
        <Grid container direction="column">
          <Grid item>
            <Typography
              sx={{ fontSize: 11, color: '#9e9e9e', whiteSpace: 'pre-wrap' }}
              variant="inherit"
            >
              PMS start date:{'    '}
            </Typography>
          </Grid>
          <Grid item>
            <Typography sx={{ fontSize: 11 }}>
              {enableHotelGroups
                ? groupPmsDates && groupPmsDates?.length && !groupPmsDates.includes(null)
                  ? format(max(groupPmsDates.map(parseISO)), YEAR_MONTH_DAY_PATTERN)
                  : null
                : pmsDate}{' '}
              UTC
            </Typography>
          </Grid>
        </Grid>
      )}
    </Stack>
  );
};
//
export default DataUpdatedLabel;
